/**
 * SPRINT-035B: Lightweight checkout telemetry.
 *
 * Uses navigator.sendBeacon in production (fire-and-forget, survives page unload).
 * Falls back to console.log in development. Silent fail on beacon error.
 */

const TELEMETRY_ENDPOINT = `${import.meta.env.VITE_API_URL ?? ''}/v4/checkout/telemetry`;
const IS_DEV = import.meta.env.DEV;

export function emitCheckoutEvent(name: string, payload: Record<string, unknown>): void {
  const event = {
    name,
    timestamp: new Date().toISOString(),
    ...payload,
  };

  if (IS_DEV) {
    console.log('[checkout:telemetry]', name, event);
    return;
  }

  try {
    navigator.sendBeacon(TELEMETRY_ENDPOINT, JSON.stringify(event));
  } catch {
    // Silent fail — telemetry must never break the checkout flow
  }
}

export const checkoutTelemetry = {
  sessionLoaded(sessionId: string): void {
    emitCheckoutEvent('checkout.session_loaded', { session_id: sessionId });
  },

  methodSelected(sessionId: string, productKey: string): void {
    emitCheckoutEvent('checkout.method_selected', { session_id: sessionId, product_key: productKey });
  },

  attemptCreated(sessionId: string, attemptId: string, productKey: string): void {
    emitCheckoutEvent('checkout.attempt_created', {
      session_id: sessionId,
      attempt_id: attemptId,
      product_key: productKey,
    });
  },

  attemptFailed(sessionId: string, attemptId: string | undefined, errorCode: string): void {
    emitCheckoutEvent('checkout.attempt_failed', {
      session_id: sessionId,
      attempt_id: attemptId,
      error_code: errorCode,
    });
  },

  redirectStarted(sessionId: string, productKey: string): void {
    emitCheckoutEvent('checkout.redirect_started', {
      session_id: sessionId,
      product_key: productKey,
    });
  },

  pollTimeout(sessionId: string): void {
    emitCheckoutEvent('checkout.poll_timeout', { session_id: sessionId });
  },

  completed(sessionId: string, durationMs: number): void {
    emitCheckoutEvent('checkout.completed', {
      session_id: sessionId,
      duration_ms: durationMs,
    });
  },

  cancelled(sessionId: string): void {
    emitCheckoutEvent('checkout.cancelled', { session_id: sessionId });
  },
};
